import _ from 'lodash';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Chip } from 'react-scale/components/design-system/product';

import { useLocation } from 'react-router-dom';
// types
import { ConfigType } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { IUserJson } from '@scale/shared/types/user';
import { IProjectJson } from '@scale/shared/types/project';
import {
  validateConfig,
  IConfigValidationMessageAndName,
  ConfigValidationMessageType,
} from '@scale/shared/configValidation';
// other
import useProject from 'react-scale/corp/hooks/useProject';
// styling
import { ClassNameMap, makeStyles } from 'design-system/styles';
import { motion, AnimatePresence } from 'framer-motion';
// components
import { createSections, ISection } from './NiceConfigsSections';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spinner from 'design-system/Spinner';
import { SectionUI } from './NiceConfigsUI';
import { useParams } from 'react-router';
import { getConfigQuery, useConfigData, IConfigMeta, fetchConfig } from './useConfigData';
import { getConfigAttributes } from './useConfigAttributes';
import { recursiveExtract } from '@scale/shared/config/utils';
import useInternalProject from 'react-scale/corp/hooks/useInternalProjectId';
import { Colors } from 'react-scale/components/legacy-css/global_css';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import pluralize from 'pluralize';
import { isRapidProject } from 'react-scale/studio/helpers';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

// re-used from current implementation
// configs can't be null since we checked upstream
export const getConfigDraft = (configs: Record<string, any> | null) => recursiveExtract(configs!);

const OtherProjectChips = (props: {
  projectKeys: string[];
  handleDelete: (projectId: string) => void;
}) => {
  const { projectKeys, handleDelete } = props;
  return (
    <div>
      <span>Other subprojects to modify: </span>
      {projectKeys.map(projectKey => (
        <Chip
          key={`other-project-chip-${projectKey}`}
          label={projectKey}
          variant="outlined"
          onDelete={() => handleDelete(projectKey)}
        />
      ))}
    </div>
  );
};

// eslint-disable-next-line complexity
const NiceConfigs = (props: {
  user: IUserJson;
  type: string;
  projectId?: string;
  minimalUI?: boolean;
}) => {
  // parse the url
  const urlParams: Record<string, any> = useParams();

  const projectID = props.projectId || (props.type === ConfigType.Project ? urlParams.id : null);
  // state
  const [configMeta] = useState<IConfigMeta>({
    type: props.type,
    id: projectID,
    taskType: urlParams.taskType,
  });
  const [project] = useProject(projectID);
  const [internalProjectId] = useInternalProject(
    project && isRapidProject(project) && !project.isInternalProject ? project._id : null,
  );
  const [sections, setSections] = useState<ISection[]>([] as ISection[]);
  const [activeScrollName, setActiveScrollName] = useState<string | undefined>(undefined);
  const location = useLocation();
  const [warnings, setWarnings] = useState<IConfigValidationMessageAndName[]>(
    [] as IConfigValidationMessageAndName[],
  );
  const [isError, setError] = useState(false);
  const [isWarningPane, toggleWarningPane] = useState(false);
  const [isAboutPane, toggleAboutPane] = useState(false);
  const classes = useStyles();

  const { trySaveChanges, newSetValue, loading, saving, configs, triggerGetConfigs, changes } =
    useConfigData(props.user.email, props.type, configMeta, project);

  const acpBenchmarkWallProject = useUnleashFeatureFlag(
    'acp-nonreadonly-enabled',
    {
      projectId: projectID,
    },
    false,
  );

  // for projects with subprojects
  const [otherProjectsToModify, setOtherProjectsToModify] = useState<string[]>(
    Object.keys(project?.subprojectMap ?? {}),
  );
  useEffect(() => {
    setOtherProjectsToModify(Object.keys(project?.subprojectMap ?? {}));
  }, [project?.subprojectMap]);
  const handleOtherProjectDelete = (pKey: string) => {
    setOtherProjectsToModify(otherProjectsToModify.filter(p => p !== pKey));
  };
  const shouldShowOtherProjectsToModify = isRapidProject(project);
  const [savingOtherProjects, setSavingOtherProjects] = useState<boolean>(false);

  // TODO
  const trySaveOtherProjectChanges = async () => {
    if (otherProjectsToModify.length === 0) {
      return;
    }
    setSavingOtherProjects(true);

    for (const projectKey of otherProjectsToModify) {
      const projectId = _.get(project?.subprojectMap, projectKey);
      const configMeta = {
        id: projectId,
        type: props.type,
        taskType: urlParams.taskType,
      };
      const configAttributes =
        getConfigAttributes(
          configMeta,
          // we deconstruct some attributes here since we actually care about the subprojects' values and not the main project's
          // the following fields happen to be the same for subprojects though so we can avoid fetching the subproject
          project ? _.pick(project, 'taskType', 'taskCustomer') : undefined,
        ) ?? {};
      const { version } = await fetchConfig(configMeta, configAttributes);

      const query = getConfigQuery(configMeta, configAttributes);
      const body = {
        changes, // changes from current project
        query,
        lastVersion: version ?? -1,
        user: props.user.email,
        type: props.type,
        attributes: configAttributes,
      };
      try {
        await apiFetch(null, `corp-api/lookup/${projectId}/config`, {
          method: 'POST',
          body,
        });
        setSavingOtherProjects(false);
      } catch (error) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
      }
    }
    setSavingOtherProjects(false);
  };

  // get warnings
  const getConfigValidation = () => {
    const configDraft = getConfigDraft(configs);
    // project can't be null since we checked before calling this fn
    const taskType = project ? project.taskType : configMeta.taskType ? configMeta.taskType : null;
    const { isError, validatorMessages } = validateConfig(
      configDraft,
      taskType as TaskType,
      !!acpBenchmarkWallProject,
    );

    setWarnings(validatorMessages);
    setError(isError);
    if (_.isEmpty(validatorMessages)) {
      return { isError };
    }
    return {
      isError,
    };
  };

  // get configs
  useEffect(() => {
    if (!configMeta || (props.type === ConfigType.Project && !project)) {
      return;
    }
    triggerGetConfigs();
  }, [configMeta, project]);

  // re-run configs validation if updated
  useEffect(() => {
    if (!configs || !configMeta || (props.type === ConfigType.Project && !project)) {
      return;
    }
    getConfigValidation();
  }, [configs]);

  // set sections
  useEffect(() => {
    if (!configs || !configMeta || (props.type === ConfigType.Project && !project)) {
      return;
    }
    const newSections = createSections(configs, project);
    setSections(newSections);
  }, [configs]);

  // refs for navigation scroll
  const itemsRef = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!configs) {
      return;
    }

    if (sections.length === 0) {
      return;
    }
    itemsRef.current = itemsRef.current.slice(0, sections.length);
  }, [configs, sections]);

  useEffect(() => {
    const { hash } = location;
    setActiveScrollName(hash.replace('#', ''));
  }, [location]);

  const [numErrors, numWarnings] = useMemo(() => {
    return _.partition(warnings, w => w.type === ConfigValidationMessageType.Error).map(
      partitionedByType => partitionedByType.length,
    );
  }, [warnings]);

  // UI
  if (loading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div>
          <Spinner />
          <p>Fetching config...</p>
        </div>
      </div>
    );
  }
  return (
    <div className={classes.wrapper}>
      {!props.minimalUI && (
        <div className={classes.left}>
          {configMeta && (
            <div>
              <div>
                <Link
                  href={
                    props.type === ConfigType.TaskType
                      ? '/task_type_manager'
                      : `/lookup/${configMeta.id}`
                  }
                >
                  <div className={classes.projectLink}>
                    <FontAwesomeIcon icon="arrow-left" />
                    <span style={{ marginLeft: 8 }}>
                      {props.type === ConfigType.TaskType
                        ? 'Back to task type manager'
                        : 'Back to lookup'}
                    </span>
                  </div>
                </Link>
              </div>
              <Nav
                type={props.type}
                sections={sections}
                configMeta={configMeta}
                project={project}
                internalProjectId={internalProjectId}
                activeScrollName={activeScrollName}
              />
              <div className={classes.branding} onClick={() => toggleAboutPane(!isAboutPane)}>
                <div style={{ fontWeight: 500, lineHeight: '24px' }}>✨Nice Configs</div>
                <div className={classes.tag}>BETA</div>
              </div>
            </div>
          )}
        </div>
      )}
      <div className={props.minimalUI ? classes.rightMinimalUI : classes.right}>
        {loading ? (
          <div>
            <Spinner />
            <p>loading...</p>
          </div>
        ) : (
          <div style={{ paddingBottom: 64 }}>
            {shouldShowOtherProjectsToModify && (
              <OtherProjectChips
                projectKeys={otherProjectsToModify}
                handleDelete={handleOtherProjectDelete}
              />
            )}
            {props.type !== ConfigType.Project && (
              <InfoBanner
                message={
                  props.type === ConfigType.TaskType
                    ? 'This config applies to an entire task type, not just 1 project. Proceed with caution.'
                    : props.type === ConfigType.TaskCustomerType
                    ? 'This config applies to a customer <> task type combination, not just 1 project. Just FYI :)'
                    : props.type === ConfigType.Default
                    ? 'This is the global default config. Proceed with caution.'
                    : props.type === ConfigType.TaskCustomer
                    ? 'This config applies to an entire customer, not just 1 project. Proceed with caution.'
                    : ''
                }
              />
            )}
            {sections.map((section, i) => (
              <div
                key={i}
                ref={el => {
                  itemsRef.current[i] = el;
                }}
                id={section.title.replace(/\s+/g, '').toLowerCase()}
                style={{ padding: '20px 0' }}
              >
                <SectionUI
                  title={section.title}
                  description={section.description}
                  configs={section.children}
                  changes={changes}
                  handleChange={newSetValue}
                  projectId={projectID}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {!loading && (
        <button
          className={classes.warnings}
          style={{
            background: numErrors ? Colors.ScaleRed : numWarnings ? '#F8BB06' : '#E6E6E6',

            color: numErrors ? 'white' : 'black',
          }}
          onClick={() => toggleWarningPane(!isWarningPane)}
        >
          {isError ? `${numErrors} errors` : `${warnings.length} warnings`}
        </button>
      )}
      <AnimatePresence>
        {isAboutPane && <AboutPane handleClose={() => toggleAboutPane(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {isWarningPane && (
          <WarningPane
            warnings={warnings}
            handleClose={() => toggleWarningPane(false)}
            isError={isError}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!_.isEmpty(changes) && (
          <SaveToast
            changes={changes}
            handleSave={() => {
              trySaveChanges();
              if (shouldShowOtherProjectsToModify) {
                trySaveOtherProjectChanges();
              }
            }}
            saving={saving || savingOtherProjects}
            isError={isError}
            toggleWarningPane={toggleWarningPane}
            subprojectKeys={otherProjectsToModify}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const NiceConfigSection = (props: {
  parent: string;
  sections: ISection[];
  classes: ClassNameMap;
  activeScrollName?: string;
}) => {
  const { parent, sections, classes, activeScrollName } = props;
  const title = parent.toUpperCase();
  const relevantSections = sections.filter(section => section.parent === parent);
  return (
    <div>
      {relevantSections.length ? <h5 className={classes.sectionTitle}>{title}</h5> : <></>}
      {relevantSections.map((section, i) => (
        <a
          key={i}
          className={classes.navItem}
          style={{
            background:
              section.title.replace(/\s+/g, '').toLowerCase() === activeScrollName
                ? '#FDF2FC'
                : 'transparent',
          }}
          href={`#${section.title.replace(/\s+/g, '').toLowerCase()}`}
        >
          {section.title}
        </a>
      ))}
    </div>
  );
};

const Nav = (props: {
  sections: ISection[];
  type: string;
  configMeta: IConfigMeta;
  project: IProjectJson | null;
  internalProjectId: string | null;
  activeScrollName: string | undefined;
}) => {
  const { sections, type, configMeta, project, internalProjectId, activeScrollName } = props;

  const classes = useStyles();

  return (
    <div className={classes.navInnerWrapper}>
      <div style={{ marginBottom: 16, paddingLeft: 8 }}>
        <div className={classes.tag} style={{ marginBottom: 4 }}>
          {type === ConfigType.Project
            ? 'project'
            : type === ConfigType.TaskType
            ? 'task type'
            : type === ConfigType.TaskCustomerType
            ? 'customer & task type'
            : 'customer'}
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>
          {type === ConfigType.Project
            ? project!.name
            : type === ConfigType.TaskType || type === ConfigType.TaskCustomerType
            ? _.capitalize(configMeta.taskType)
            : type === ConfigType.Default
            ? 'Global Default'
            : 'Customer'}
        </h2>
        <p style={{ fontSize: 12, color: '#808080', margin: 0 }}>{configMeta.id}</p>
        {internalProjectId && (
          <>
            <h2 style={{ fontSize: 12, margin: 0, paddingTop: 12 }}>Internal Project</h2>
            <p style={{ fontSize: 12, color: '#808080', margin: 0 }}>{internalProjectId}</p>
          </>
        )}
      </div>
      <NiceConfigSection
        parent="General"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Grader"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Benchmarks"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="General"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Promoting"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Disabling"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Linting"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Guided Workflow"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Prebuild"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Task Holding"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Claim"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Throttle"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Task Redo"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <NiceConfigSection
        parent="Text Collection"
        sections={sections}
        classes={classes}
        activeScrollName={activeScrollName}
      />
      <div className={classes.navMask} />
    </div>
  );
};

export const WarningPane = (props: {
  warnings: IConfigValidationMessageAndName[];
  handleClose: () => void;
  isError: boolean;
  additionalText?: string;
}) => {
  const classes = useStyles();

  const numErrors = props.isError
    ? props.warnings.filter(w => w.type.toLowerCase() === 'error').length
    : 0;
  const numWarnings = props.warnings.filter(w => w.type.toLowerCase() === 'warn').length;
  return (
    <motion.div
      className={classes.warningPane}
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 5, opacity: 0 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <p style={{ fontWeight: 600, margin: 0 }}>
          {props.isError
            ? `${numErrors} errors, ${numWarnings} warnings`
            : `${numWarnings} warnings`}
        </p>

        <button className={classes.closeButton} onClick={props.handleClose}>
          <FontAwesomeIcon icon="times" />
        </button>
      </div>
      {props.additionalText && (
        <p style={{ fontWeight: 600, margin: 0 }}>{`${props.additionalText}`}</p>
      )}

      {props.warnings
        // put errors at the top
        .sort((a, b) => (a.type > b.type ? 1 : b.type > a.type ? -1 : 0))
        .map((warning, index) => (
          <div key={index} className={classes.warningItem}>
            <p
              className={classes.warningName}
              style={{
                color: warning.type.toLowerCase() === 'error' ? Colors.ScaleRed : '#808080',
              }}
            >
              {warning.type.toLowerCase() === 'error'
                ? `Error: ${warning.validatorName}`
                : warning.validatorName}
            </p>
            <p className={classes.warningText}>{warning.text}</p>
          </div>
        ))}
    </motion.div>
  );
};

export const SaveToast = (props: {
  changes: Record<string, any>;
  handleSave: () => void;
  saving: boolean;
  isError: boolean;
  toggleWarningPane?: (x?: any) => void;
  subprojectKeys?: string[];
}) => {
  const classes = useStyles();

  const { changes, handleSave, saving, isError, toggleWarningPane, subprojectKeys } = props;

  return (
    <motion.div
      className={classes.saveToast}
      initial={{ y: 50 }}
      animate={{ y: 0 }}
      exit={{ y: 50 }}
    >
      <div className={classes.numChanges}>
        <p className={classes.changesText}>
          {pluralize('change', _.size(changes), true)}
          {subprojectKeys?.length ? ` +(${subprojectKeys.join(', ')})` : ''}
        </p>
        {isError && (
          <p
            onClick={() => toggleWarningPane && toggleWarningPane((x: boolean) => !x)}
            className={classes.errorText}
          >
            Fix errors before saving
          </p>
        )}
        {!isError && (
          <p className={classes.changesText} style={{ opacity: 0.6 }}>
            May take a minute to be reflected upon saving.
          </p>
        )}
      </div>
      <div>
        <button className={classes.saveButton} disabled={saving || isError} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </motion.div>
  );
};

const AboutPane = (props: { handleClose: () => void }) => {
  const classes = useStyles();

  return (
    <motion.div
      className={classes.aboutPane}
      initial={{ y: 10, x: 24, opacity: 0 }}
      animate={{ y: 0, x: 24, opacity: 1 }}
      exit={{ y: 5, x: 24, opacity: 0 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <p style={{ fontWeight: 600, margin: 0 }}>Welcome to ✨Nice Configs!</p>
        <button className={classes.closeButton} onClick={props.handleClose}>
          <FontAwesomeIcon icon="times" />
        </button>
      </div>
      <div>
        <p style={{ margin: 0, marginTop: 8, lineHeight: 1.5 }}>
          An attempt at making configs less bad.
        </p>
        <p style={{ margin: 0, marginTop: 8, lineHeight: 1.5 }}>
          "Beta" because this is just a start: Nice Configs currently only supports the (old)
          Benchmark & Disabling configs. Other configs can still be set from the project page.
        </p>
        <p style={{ margin: 0, marginTop: 8, lineHeight: 1.5 }}>
          Ideas/feedback? Ping @sophia. Questions? Ping #corp.
        </p>
        <p style={{ margin: 0, marginTop: 8, lineHeight: 1.5 }}>
          Dedicated to Jeffrey Li, original author of <i>The QM Playbook</i> (2020).
        </p>
      </div>
    </motion.div>
  );
};

export const InfoBanner = (props: { message: string }) => {
  const classes = useStyles();

  return (
    <div className={classes.banner}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FontAwesomeIcon icon="exclamation-triangle" color="#F8BB06" />
        <span style={{ marginLeft: 16 }}>{props.message}</span>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: 1240,
    margin: '0 auto',
  },
  left: {
    width: 300,
    height: '100%',
    flexShrink: 0,
    padding: 24,
    position: 'fixed',
    zIndex: 1,
    top: 0,
  },
  right: {
    marginLeft: 300, // same as nav width
    flexGrow: 1,
    padding: '24px 40px',
    paddingTop: 50,
  },
  rightMinimalUI: {
    flexGrow: 1,
  },
  navInnerWrapper: {
    marginTop: 32,
    paddingBottom: 32,
    height: 'calc(100vh - 106px - 32px)',
    overflowY: 'scroll',
    position: 'relative',
  },
  navMask: {
    position: 'fixed',
    bottom: 44,
    width: 300,
    height: 'calc(100vh - 106px - 32px)',
    pointerEvents: 'none',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 80%, rgba(255,255,255,0.9) 100%)',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.7px',
    margin: 0,
    padding: 8,
    marginTop: 24,
  },
  navItem: {
    display: 'block',
    color: 'black',
    padding: '6px 8px',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    borderRadius: 4,
    opacity: 0.65,
    transition: 'all 120ms ease-out',
    '&:hover': {
      opacity: 1,
    },
  },
  projectLink: {
    paddingLeft: 8,
    fontSize: 12,
    lineHeight: '16px',
    color: '#404040',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    opacity: 0.6,
    transition: 'opacity 150ms ease-out',
    '&:hover': {
      opacity: 1,
    },
  },
  branding: {
    position: 'fixed',
    bottom: 12,
    background: 'white',
    padding: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: 252, // 300 - 48
    borderRadius: 4,
    cursor: 'pointer',
    transition: 'background 150ms ease-out',
    '&:hover': {
      background: '#f2f2f2',
    },
  },
  tag: {
    padding: '2px 4px',
    fontSize: 11,
    fontWeight: 500,
    background: '#f2f2f2',
    borderRadius: 4,
    color: '#808080',
    textTransform: 'uppercase',
    display: 'inline-block',
    letterSpacing: '0.5px',
  },
  aboutPane: {
    position: 'fixed',
    bottom: 54,
    padding: 16,
    fontSize: 12,
    lineHeight: '20px',
    width: 304,
    maxHeight: 400,
    overflowY: 'scroll',
    background: 'white',
    borderRadius: 8,
    boxShadow: '0 3px 12px rgba(0,0,0,0.1)',
    zIndex: 10,
  },
  warnings: {
    position: 'fixed',
    bottom: 16,
    right: 16,
    margin: 0,
    fontSize: 12,
    lineHeight: '20px',
    padding: '2px 8px',
    borderRadius: 50,
    cursor: 'pointer',
    outline: 'none',
    border: 'none',
    opacity: 0.85,
    transition: 'opacity 150ms ease-out',
    '&:hover': {
      opacity: 1,
    },
  },
  warningPane: {
    position: 'fixed',
    bottom: 50,
    right: 16,
    padding: 16,
    fontSize: 12,
    lineHeight: '20px',
    width: 320,
    maxHeight: 400,
    overflowY: 'scroll',
    background: 'white',
    borderRadius: 8,
    boxShadow: '0 3px 12px rgba(0,0,0,0.1)',
    zIndex: 10,
  },
  closeButton: {
    outline: 'none',
    border: 'none',
    height: 24,
    width: 24,
    cursor: 'pointer',
    background: 'white',
    opacity: 0.4,
    borderRadius: '50%',
    transition: 'all 150ms ease-out',
    '&:hover': {
      background: '#f2f2f2',
      opacity: 0.6,
    },
  },
  warningItem: {
    padding: '6px 0',
  },
  warningName: {
    margin: 0,
    lineHeight: '16px',
  },
  warningText: {
    margin: 0,
    lineHeight: '16px',
  },
  saveToast: {
    position: 'fixed',
    bottom: 16,
    left: '50%',
    background: 'white',
    padding: 12,
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transform: 'translateX(-50%)',
  },
  numChanges: {
    fontSize: 12,
    marginRight: 48,
    marginLeft: 4,
  },
  changesText: {
    lineHeight: '16px',
    margin: 0,
  },
  errorText: {
    lineHeight: '16px',
    margin: 0,
    color: 'var(--color-RemoRed)',
    cursor: 'pointer',
    '& hover': {
      color: 'black',
    },
  },
  saveButton: {
    outline: 'none',
    border: 'none',
    fontSize: 14,
    fontWeight: 500,
    padding: 8,
    width: 112,
    background: '#DD45D3',
    color: 'white',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 150ms ease-out',
    '&:hover': {
      background: '#C634C3',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  banner: {
    background: 'rgba(248, 187, 6, 0.1)',
    padding: 16,
    fontSize: 14,
    lineHeight: '20px',
    marginBottom: 20,
    marginTop: 20,
    borderRadius: 8,
  },
});

export default NiceConfigs;
